import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook to detect if text content is visually clamped/truncated by CSS
 * @param content - The content string to monitor for changes
 * @returns Object with ref to attach to element and boolean indicating if text is clamped
 */
export function useTextClamped<T extends HTMLElement = HTMLElement>(
  content: React.ReactNode
) {
  const [isTextClamped, setIsTextClamped] = useState(false);
  const textRef = useRef<T>(null);

  useEffect(() => {
    const checkIfClamped = () => {
      if (textRef.current) {
        const element = textRef.current;
        // Check both horizontal and vertical overflow
        // Horizontal: for truncate, text-ellipsis
        // Vertical: for line-clamp-*
        const isHorizontallyClamped = element.scrollWidth > element.clientWidth;
        const isVerticallyClamped = element.scrollHeight > element.clientHeight;
        setIsTextClamped(isHorizontallyClamped || isVerticallyClamped);
      }
    };

    checkIfClamped();

    // Check again on resize
    window.addEventListener('resize', checkIfClamped);
    return () => window.removeEventListener('resize', checkIfClamped);
  }, [content]);

  return { textRef, isTextClamped };
}
